import { getUserToken } from "utils/helperFunctions";
import {
  REACT_APP_AI_SERVICE_URL,
  REACT_APP_API_URL,
} from "utils/environmentVariables";
import { store } from "utils/redux/store";

// ---------------------- Types ----------------------

/**
 * Typed error thrown by aiServiceFetch for non-2xx responses.
 * Preserves the HTTP status and the parsed response body so callers can
 * branch on specific codes (e.g. 409 WINDOW_OPEN) without re-parsing.
 */
export class AiServiceError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = "AiServiceError";
    this.status = status;
    this.data = data;
  }
}

export interface StreamingCallbacks {
  onChunk?: (content: string) => void;
  onComplete?: (fullResponse: string) => void;
  onError?: (error: Error) => void;
  onToolStart?: (name: string, label: string) => void;
  onToolEnd?: (name: string, success: boolean) => void;
}

export interface StreamError extends Error {
  status?: number;
  data?: unknown;
}

// ---------------------- Route Mapping ----------------------
// Maps Laravel API endpoints to AI Service endpoints
const ROUTE_MAP: Record<string, string> = {
  "/talentInsights/generateInstructions": "/v1/chat/generateInstructions",
  "/talentInsights/generateTEAMscanInstructions":
    "/v1/chat/generateTEAMscanInstructions",
  "/talentInsights/generatePersonalityInstructions":
    "/v1/chat/generatePersonalityInstructions",
  "/talentInsights/chatbot": "/v1/chat/sendMessage",
  "/talentInsights/coachBoConversations": "/v1/chat/conversations",
};

// ---------------------- Feature Flag Check ----------------------
/**
 * Check if AI Service should be used based on ConfigCat flags
 * Uses the configCatFlags from Redux store
 */
function shouldUseAIService(): boolean {
  const state = store.getState();
  const configCatFlags = state.global.configCatFlags;

  // Check if AI Service is enabled via ConfigCat
  return configCatFlags["aiServiceChatEnabled"] === true;
}

// ---------------------- Types ----------------------
interface StreamingRequestConfig {
  url: string;
  headers: HeadersInit;
  body: Record<string, unknown>;
  /** ID returned in the response (threadId for authenticated, encryptedThreadId for loginless) */
  responseThreadId: string;
}

// ---------------------- Request Helpers ----------------------
export function getAuthHeaders(): HeadersInit {
  const token = getUserToken();
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };
}

/**
 * Transform Laravel request payload to AI Service format
 */
function transformRequestPayload(
  endpoint: string,
  payload: Record<string, unknown>
): Record<string, unknown> {
  // Transform generateInstructions request
  if (endpoint.includes("generateInstructions")) {
    return {
      chatType: payload.chatType,
      userContext: {
        firstName: (payload.userInfo as Record<string, unknown>)?.firstName,
        lastName: (payload.userInfo as Record<string, unknown>)?.lastName,
        jobTitle: (payload.userInfo as Record<string, unknown>)?.jobTitle,
        companyName: (payload.userInfo as Record<string, unknown>)?.companyName,
        eppData: payload.eppData,
        caseFile: payload.caseFile,
        coachingPlan: payload.coachingPlan,
        language: payload.language ?? "English",
        style: payload.style ?? "normal",
      },
      companyInstructions: payload.companyInstructions,
      documentContent: payload.documentContent,
      reportData: payload.reportData,
    };
  }

  // Transform TEAMscan request
  if (endpoint.includes("TEAMscan")) {
    return {
      teamName: payload.teamName,
      teamData: payload.teamData,
      userContext: {
        firstName: (payload.userInfo as Record<string, unknown>)?.firstName,
        lastName: (payload.userInfo as Record<string, unknown>)?.lastName,
        language: payload.language ?? "English",
      },
    };
  }

  // Transform Personality request
  if (endpoint.includes("Personality")) {
    return {
      personalityData: payload.personalityData,
      userContext: {
        firstName: (payload.userInfo as Record<string, unknown>)?.firstName,
        lastName: (payload.userInfo as Record<string, unknown>)?.lastName,
        language: payload.language ?? "English",
      },
    };
  }

  // Transform sendMessage request (chatbot)
  if (endpoint.includes("chatbot")) {
    return {
      threadId: payload.threadId,
      message:
        (payload.messages as Array<{ role: string; content: string }>)?.slice(
          -1
        )[0]?.content || "",
      conversationHistory: (
        payload.messages as Array<{ role: string; content: string }>
      )?.slice(0, -1),
    };
  }

  return payload;
}

/**
 * Transform AI Service response to match Laravel API response format
 */
function transformResponse(
  endpoint: string,
  response: Record<string, unknown>,
  useAIService: boolean
): Record<string, unknown> {
  if (!useAIService) {
    return response;
  }

  // AI Service wraps responses in { success: true, data: {...} }
  const data = response.data as Record<string, unknown>;

  // For generateInstructions, extract threadId
  if (endpoint.includes("generateInstructions")) {
    return {
      threadId: data?.threadId,
    };
  }

  // For conversations list
  if (
    endpoint.includes("coachBoConversations") &&
    !endpoint.includes("{threadId}")
  ) {
    return data as Record<string, unknown>;
  }

  return data || response;
}

// ---------------------- Main Request Function ----------------------
/**
 * Smart request function that routes to AI Service or Laravel API
 * based on ConfigCat feature flags
 */
export async function aiServiceRequest<T = Record<string, unknown>>(
  laravelEndpoint: string,
  options?: RequestInit
): Promise<T> {
  const useAIService = shouldUseAIService();

  // Determine base URL and endpoint
  let baseUrl: string;
  let endpoint: string;

  if (useAIService && ROUTE_MAP[laravelEndpoint]) {
    baseUrl = REACT_APP_AI_SERVICE_URL;
    endpoint = ROUTE_MAP[laravelEndpoint];
  } else {
    baseUrl = REACT_APP_API_URL;
    endpoint = laravelEndpoint;
  }

  const url = `${baseUrl}${endpoint}`;

  // Transform payload if using AI Service
  let body = options?.body;
  if (useAIService && body && typeof body === "string") {
    try {
      const payload = JSON.parse(body);
      const transformedPayload = transformRequestPayload(
        laravelEndpoint,
        payload
      );
      body = JSON.stringify(transformedPayload);
    } catch {
      // Keep original body if parsing fails
    }
  }

  const response = await fetch(url, {
    ...options,
    headers: getAuthHeaders(),
    body,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      (errorData as { error?: string }).error || `HTTP ${response.status}`
    );
  }

  const jsonResponse = await response.json();
  return transformResponse(laravelEndpoint, jsonResponse, useAIService) as T;
}

/**
 * Shared fetch helper for non-streaming AI Service requests.
 * Handles URL construction, auth headers, and error body parsing.
 */
export async function aiServiceFetch<T = unknown>(
  path: string,
  options: {
    method?: "GET" | "POST" | "PUT" | "DELETE";
    body?: Record<string, unknown>;
  } = {}
): Promise<T> {
  const { method = "POST", body } = options;
  const url = `${REACT_APP_AI_SERVICE_URL}${path}`;

  const response = await fetch(url, {
    method,
    headers: getAuthHeaders(),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new AiServiceError(
      (errorData as { error?: string }).error ||
        `AI Service error: ${response.status}`,
      response.status,
      errorData
    );
  }

  return response.json() as Promise<T>;
}

// ---------------------- Streaming Request Function ----------------------
/**
 * Send message with streaming support (authenticated flow).
 * Uses SSE for AI Service, regular request for Laravel API.
 */
export async function aiServiceStreamingRequest(
  threadId: string,
  messages: Array<{ role: "user" | "assistant"; content: string }>,
  callbacks: StreamingCallbacks
): Promise<{
  messages: Array<{ role: "user" | "assistant"; content: string }>;
  threadId: string;
}> {
  const useAIService = isUsingAIService();

  if (useAIService) {
    const lastMessage = messages[messages.length - 1]?.content || "";
    const conversationHistory = messages.slice(0, -1);
    return sendMessageWithStreaming(messages, callbacks, {
      url: `${REACT_APP_AI_SERVICE_URL}/v1/chat/sendMessage`,
      headers: getAuthHeaders(),
      body: { threadId, message: lastMessage, conversationHistory },
      responseThreadId: threadId,
    });
  } else {
    return sendMessageLegacy(threadId, messages, callbacks);
  }
}

/**
 * Send loginless message with streaming support via AI Service.
 * Uses encrypted thread token for auth instead of JWT Bearer.
 */
export async function loginlessStreamingRequest(
  encryptedThreadId: string,
  messages: Array<{ role: "user" | "assistant"; content: string }>,
  callbacks: StreamingCallbacks
): Promise<{
  messages: Array<{ role: "user" | "assistant"; content: string }>;
  threadId: string;
}> {
  return sendMessageWithStreaming(messages, callbacks, {
    url: `${REACT_APP_AI_SERVICE_URL}/v1/loginless/chatbot/loginless`,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: {
      token: encryptedThreadId,
      messages,
    },
    responseThreadId: encryptedThreadId,
  });
}

const STREAM_TIMEOUT_MS = 90_000;

function normalizeStreamError(error: unknown): Error {
  if (!(error instanceof Error)) return new Error(String(error));
  if (error.name === "AbortError") return new Error("Stream timed out");
  return error;
}

/**
 * Core SSE streaming function used by both authenticated and loginless flows.
 * Supports tool_start/tool_end SSE events for tool call UI feedback.
 */
async function sendMessageWithStreaming(
  messages: Array<{ role: "user" | "assistant"; content: string }>,
  callbacks: StreamingCallbacks,
  config: StreamingRequestConfig
): Promise<{
  messages: Array<{ role: "user" | "assistant"; content: string }>;
  threadId: string;
}> {
  let fullResponse = "";
  const streamedToolCalls: { name: string; label: string; success: boolean }[] =
    [];
  const pendingToolLabel = new Map<string, string>();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), STREAM_TIMEOUT_MS);

  const buildAssistantMessage = () => ({
    role: "assistant" as const,
    content: fullResponse,
    ...(streamedToolCalls.length > 0 && { toolCalls: streamedToolCalls }),
  });

  try {
    const response = await fetch(config.url, {
      method: "POST",
      headers: config.headers,
      body: JSON.stringify(config.body),
      signal: controller.signal,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const streamError: StreamError = new Error(
        (errorData as { error?: string }).error || `HTTP ${response.status}`
      );
      streamError.status = response.status;
      streamError.data = errorData;
      throw streamError;
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    if (!reader) {
      throw new Error("No response body");
    }

    let buffer = "";
    let currentEventType = "";
    let receivedDone = false;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");

      // Keep the last potentially incomplete line in the buffer
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (line.startsWith("event: ")) {
          currentEventType = line.slice(7).trim();
          continue;
        }

        if (line === "") {
          currentEventType = "";
          continue;
        }

        if (!line.startsWith("data: ")) continue;

        const data = line.slice(6);
        const eventType = currentEventType || "message";
        currentEventType = "";

        if (data === "[DONE]") {
          receivedDone = true;
          callbacks.onComplete?.(fullResponse);
          return {
            messages: [...messages, buildAssistantMessage()],
            threadId: config.responseThreadId,
          };
        }

        try {
          const parsed = JSON.parse(data);

          if (eventType === "tool_start") {
            pendingToolLabel.set(parsed.name, parsed.label ?? parsed.name);
            callbacks.onToolStart?.(parsed.name, parsed.label ?? parsed.name);
            continue;
          }

          if (eventType === "tool_end") {
            streamedToolCalls.push({
              name: parsed.name,
              label: pendingToolLabel.get(parsed.name) ?? parsed.name,
              success: parsed.success,
            });
            pendingToolLabel.delete(parsed.name);
            callbacks.onToolEnd?.(parsed.name, parsed.success);
            continue;
          }

          if (parsed.content) {
            fullResponse += parsed.content;
            callbacks.onChunk?.(parsed.content);
          }
        } catch {
          // Skip malformed JSON
        }
      }
    }

    // Process any remaining data in buffer
    if (buffer.startsWith("data: ")) {
      const data = buffer.slice(6);
      if (data !== "[DONE]") {
        try {
          const parsed = JSON.parse(data);
          if (parsed.content) {
            fullResponse += parsed.content;
            callbacks.onChunk?.(parsed.content);
          }
        } catch {
          // Skip malformed JSON
        }
      } else {
        receivedDone = true;
      }
    }

    if (!receivedDone) {
      throw new Error("Stream ended without completion signal");
    }

    callbacks.onComplete?.(fullResponse);
    return {
      messages: [...messages, buildAssistantMessage()],
      threadId: config.responseThreadId,
    };
  } catch (error) {
    const err = normalizeStreamError(error);
    callbacks.onError?.(err);
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Send message without streaming (Laravel API fallback)
 */
async function sendMessageLegacy(
  threadId: string,
  messages: Array<{ role: "user" | "assistant"; content: string }>,
  callbacks: StreamingCallbacks
): Promise<{
  messages: Array<{ role: "user" | "assistant"; content: string }>;
  threadId: string;
}> {
  const url = `${REACT_APP_API_URL}/talentInsights/chatbot`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ threadId, messages }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        (errorData as { error?: string }).error || `HTTP ${response.status}`
      );
    }

    const data = await response.json();
    callbacks.onComplete?.(
      data.messages?.[data.messages.length - 1]?.content || ""
    );
    return data;
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    callbacks.onError?.(err);
    throw err;
  }
}

// ---------------------- Utility Functions ----------------------
/**
 * Check if currently using AI Service (for debugging/logging)
 */
export function isUsingAIService(): boolean {
  return shouldUseAIService();
}

/**
 * Resolves once ConfigCat flags have loaded so callers pick the right backend.
 * Calls made before the flags arrive would otherwise fall back to Laravel.
 * Gives up after `timeoutMs` (e.g. if the flag load fails) and resolves anyway.
 */
export function waitForConfigCatFlags(timeoutMs = 5000): Promise<void> {
  const isSettled = () => {
    const status = store.getState().global.getConfigCatFlagsStatus;
    return status === "succeeded" || status === "failed";
  };

  if (isSettled()) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timeout);
      unsubscribe();
      resolve();
    };
    const timeout = setTimeout(done, timeoutMs);
    const unsubscribe = store.subscribe(() => {
      if (isSettled()) {
        done();
      }
    });
  });
}

/**
 * Force use AI Service for testing (bypasses feature flag)
 * Only works in development
 */
export function forceUseAIService(force: boolean): void {
  if (process.env.NODE_ENV === "development") {
    (
      window as unknown as { __FORCE_AI_SERVICE__: boolean }
    ).__FORCE_AI_SERVICE__ = force;
  }
}
